import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { StepTitle, OField, inputCls, NavButtons } from "@/components/onboard/ui";
import { DeviceMobile, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export function StepPhone({ token, state, onSaved, onBack }) {
  const c = state.wizard.contact || {};
  const [phone, setPhone] = useState(c.phone || state.phone || "");
  const [email, setEmail] = useState(c.email || state.email || "");
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => { if (wait <= 0) return undefined; const t = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(t); }, [wait]);

  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/onboard/${token}/sms/send`, { phone, email });
      setSent(data); setCode(""); setWait(30);
      toast.success(data.sms && data.email ? "Code sent by text and email" : data.email ? "Code sent to your email" : "Code sent by text");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const verify = async (v = code) => {
    if (v.length !== 6 || loading) return;
    setLoading(true);
    try { const { data } = await api.post(`/onboard/${token}/sms/verify`, { code: v }); toast.success(sent?.sms ? "Phone verified" : "Verified"); onSaved(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setCode(""); }
    finally { setLoading(false); }
  };
  const onCode = (e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setCode(v); if (v.length === 6) verify(v); };

  return (
    <div>
      <StepTitle kicker="Step 2 · Secure Your Account" title="Verify Your Phone" sub="We'll send a one-time code to your mobile number and email. Your email and phone become your login for the Asterism Carrier App." />
      {!sent ? (
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <OField label="Mobile Number *"><input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className={inputCls} data-testid="ob-phone" /></OField>
          <OField label="Email *"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} data-testid="ob-email" /></OField>
        </div>
      ) : (
        <div className="max-w-md">
          <div className="mb-4 space-y-1.5 text-sm text-white/70">
            {sent.sms && <p className="flex items-center gap-2"><DeviceMobile size={20} className="text-sky-300" />Texted to <b className="text-white">{phone}</b></p>}
            {!sent.sms && <p className="text-amber-300/90 text-xs" data-testid="ob-sms-fallback-note">We couldn't text your phone right now, so we emailed your code instead.</p>}
            {sent.email && <p className="flex items-center gap-2"><EnvelopeSimple size={20} className="text-sky-300" />Emailed to <b className="text-white">{email}</b></p>}
          </div>
          <input value={code} onChange={onCode} inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="••••••"
            className="w-full h-16 rounded-2xl border border-white/15 bg-white/5 text-center text-3xl font-mono font-bold tracking-[0.6em] text-white placeholder:text-white/20 focus:outline-none focus:border-sky-400" data-testid="ob-sms-input" />
          <p className="mt-2 text-xs text-white/40">The code is checked automatically when you enter all 6 digits.</p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <button onClick={send} disabled={wait > 0 || loading} className="text-sky-300 disabled:text-white/30" data-testid="ob-resend">{wait > 0 ? `Resend in ${wait}s` : "Resend code"}</button>
            <button onClick={() => { setSent(null); setCode(""); }} className="text-white/50 hover:text-white" data-testid="ob-change-phone">Change number</button>
          </div>
        </div>
      )}
      <NavButtons onBack={onBack} onNext={sent ? () => verify() : send} loading={loading} disabled={sent ? code.length !== 6 : !phone || !email} nextLabel={sent ? "Verify Code" : "Send Code"} testid={sent ? "ob-verify-btn" : "ob-send-btn"} />
    </div>
  );
}
